/* RESPONSIBLE TEAM: team-reporting */
/* eslint-disable @intercom/intercom/no-bare-strings */
import type IntlService from 'ember-intl/services/intl';
import { type Filter, type LogicalFilter } from 'embercom/components/reporting/custom/filters';
// @ts-ignore no type declaration available for ember-copy
import { copy } from 'ember-copy';

export function and(...filters: any): LogicalFilter {
  filters = filters.compact();
  if (filters.length === 1) {
    return filters[0];
  }

  return {
    type: 'and',
    filters: filters.flatMap((f: any) => (f.type === 'and' ? f.filters : f)),
  };
}

export function or(...filters: Filter[]): Filter | LogicalFilter {
  let properties = filters.map((f: Filter) => f.data.property).uniq();
  let allAreCategoryFilters = filters.every((f: Filter) => f.type === 'category');
  if (properties.length === 1 && allAreCategoryFilters) {
    // merge the filters into a single filter
    return {
      type: 'category',
      data: {
        property: filters[0].data.property,
        attribute: filters[0].data.attribute,
        values: filters.flatMap((f: Filter) => f.data.values),
      },
    };
  } else {
    return {
      type: 'or',
      filters: filters.flatMap((f: any) => (f.type === 'or' ? f.filters : f)),
    };
  }
}

export function not(filter: any): Filter {
  filter = copy(filter, true);
  if (filter.type === 'category') {
    // The "Fin AI Agent involved" data attribute uses a filter type that doesn't
    // support "not_in_category", so if it's a Boolean category, we can just negate
    let values = filter.data.values;
    if (values.length === 1 && typeof values.firstObject === 'boolean') {
      filter.data.values = [!values.firstObject];
    } else {
      filter.type = 'not_in_category';
    }
  } else if (filter.type === 'exists') {
    filter.type = 'not_exists';
  } else {
    throw new Error(`Unsupported filter type: ${filter.type}`);
  }
  return filter;
}

const filters = {
  get chatConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Desktop'],
      },
    };
  },
  get emailConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Email'],
      },
    };
  },
  get phoneConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Phone call'],
      },
    };
  },
  get androidConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Android App'],
      },
    };
  },
  get iosConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['iOS App'],
      },
    };
  },
  get twitterConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Twitter'],
      },
    };
  },
  get facebookMessengerConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Facebook Messenger'],
      },
    };
  },
  get facebookConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Facebook'],
      },
    };
  },
  get whatsappConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['WhatsApp'],
      },
    };
  },
  get instagramConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Instagram'],
      },
    };
  },
  get phoneSwitchConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Phone switch'],
      },
    };
  },
  get smsConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['SMS'],
      },
    };
  },
  get zendeskTicketConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Zendesk ticket'],
      },
    };
  },
  get salesforceCaseConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Salesforce case'],
      },
    };
  },
  get zendeskMessagingConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Zendesk Messaging'],
      },
    };
  },
  get slackConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Slack'],
      },
    };
  },
  get unknownConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Unknown'],
      },
    };
  },
  // Main filter for resolved conversations - use this instead of individual resolution state filters
  get finResolved() {
    return {
      type: 'category',
      data: {
        property: 'fin.resolution_state',
        attribute: 'conversation.fin.resolution_state',
        values: ['confirmed_resolved', 'assumed_resolved'],
      },
    };
  },

  get finRoutedToTeam() {
    return {
      type: 'category',
      data: {
        property: 'conversation_custom_fields#ai_chatbot_resolution_state',
        attribute: 'conversation_custom_fields#ai_chatbot_resolution_state',
        values: ['routed_to_team'],
      },
    };
  },

  get finResolvedOrRoutedToTeam() {
    return {
      type: 'category',
      data: {
        property: 'conversation_custom_fields#ai_chatbot_resolution_state',
        attribute: 'conversation_custom_fields#ai_chatbot_resolution_state',
        values: ['hard_resolution', 'soft_resolution', 'routed_to_team', 'procedure_handoff'],
      },
    };
  },
  get finPending() {
    return not(this.finResolvedOrRoutedToTeam);
  },

  get finNotResolved() {
    return not(this.finResolved);
  },

  get finInvolved() {
    return {
      type: 'category',
      data: {
        property: 'fin.participated',
        attribute: 'conversation.fin.participated',
        values: [true],
      },
    };
  },
  get finNotInvolved() {
    return not(this.finInvolved);
  },

  get finOnlyResolved() {
    return and(this.finResolved, not(this.teammateWasEverInvolved));
  },

  get positiveCSAT() {
    return {
      type: 'category',
      data: {
        property: 'ai_generated_metrics.csat',
        attribute: 'conversation.ai_generated_metrics.csat',
        values: ['4', '5'],
      },
    };
  },
  get neutralCSAT() {
    return {
      type: 'category',
      data: {
        property: 'ai_generated_metrics.csat',
        attribute: 'conversation.ai_generated_metrics.csat',
        values: ['3'],
      },
    };
  },
  get negativeCSAT() {
    return {
      type: 'category',
      data: {
        property: 'ai_generated_metrics.csat',
        attribute: 'conversation.ai_generated_metrics.csat',
        values: ['1', '2'],
      },
    };
  },

  get finAndTeammate() {
    return and(
      this.finInvolved,
      this.teammateWasEverInvolved,
      not(this.finResolved),
      not(this.finProcedureHandoff),
    );
  },

  get finAndTeammateNew() {
    return and(this.finInvolved, this.finRoutedToTeam);
  },

  get teammateWasEverInvolved() {
    return {
      type: 'exists',
      data: {
        property: 'admin_participant_ids',
        attribute: 'conversation.admin_participant_ids',
      },
    };
  },
  get teammateWasNeverInvolved() {
    return not(this.teammateWasEverInvolved);
  },

  get finProcedureHandoff() {
    return {
      type: 'category',
      data: {
        property: 'fin.resolution_state',
        attribute: 'conversation.fin.resolution_state',
        values: ['procedure_handoff'],
      },
    };
  },

  get finEscalationRulesOrGuidanceApplied() {
    return {
      type: 'category',
      data: {
        property: 'conversation_custom_fields#configuration_based_escalation_reason',
        attribute: 'conversation_custom_fields#configuration_based_escalation_reason',
        values: ['guidance_applied', 'escalation_rule_applied'],
      },
    };
  },

  get finEscalationUserRequested() {
    return {
      type: 'category',
      data: {
        property: 'conversation_custom_fields#fin_escalation_user_requested',
        attribute: 'conversation_custom_fields#fin_escalation_user_requested',
        values: [true],
      },
    };
  },
};

export function wrap(filters: any) {
  // because of assumptions made elsewhere in the code, it's easier to
  // just make sure there's always a top level logical filter
  if (filters && filters.type !== 'and' && filters.type !== 'or') {
    return {
      type: 'and',
      filters: [filters],
    };
  }
  return filters;
}

export function funnelSeries(filters?: any, label?: string) {
  return {
    label,
    metric_id: 'v1.new_conversations',
    filters: wrap(filters),
  };
}

function config(name: string, filters?: any) {
  return { name, series: funnelSeries(filters, name) };
}

interface Metrics {
  conversations: number;

  Chat: number;
  Email: number;
  Phone: number;
  Android: number;
  iOS: number;
  Twitter: number;
  FacebookMessenger: number;
  Facebook: number;
  WhatsApp: number;
  Instagram: number;
  PhoneSwitch: number;
  SMS: number;
  ZendeskTicket: number;
  SalesforceCase: number;
  ZendeskMessaging: number;
  Slack: number;
  Unknown: number;

  ChatAndFinInvolved: number;
  EmailAndFinInvolved: number;
  ChatAndFinNotInvolved: number;
  EmailAndFinNotInvolved: number;
  AndroidAndFinInvolved: number;
  AndroidAndFinNotInvolved: number;
  iOSAndFinInvolved: number;
  iOSAndFinNotInvolved: number;
  TwitterAndFinInvolved: number;
  TwitterAndFinNotInvolved: number;
  PhoneAndFinInvolved: number;
  PhoneAndFinNotInvolved: number;
  FacebookMessengerAndFinInvolved: number;
  FacebookMessengerAndFinNotInvolved: number;
  FacebookAndFinInvolved: number;
  FacebookAndFinNotInvolved: number;
  WhatsAppAndFinInvolved: number;
  WhatsAppAndFinNotInvolved: number;
  InstagramAndFinInvolved: number;
  InstagramAndFinNotInvolved: number;
  PhoneSwitchAndFinInvolved: number;
  PhoneSwitchAndFinNotInvolved: number;
  SMSAndFinInvolved: number;
  SMSAndFinNotInvolved: number;
  ZendeskTicketAndFinInvolved: number;
  ZendeskTicketAndFinNotInvolved: number;
  SalesforceCaseAndFinInvolved: number;
  SalesforceCaseAndFinNotInvolved: number;
  ZendeskMessagingAndFinInvolved: number;
  ZendeskMessagingAndFinNotInvolved: number;
  SlackAndFinInvolved: number;
  SlackAndFinNotInvolved: number;
  UnknownAndFinInvolved: number;
  UnknownAndFinNotInvolved: number;

  FinInvolved: number;
  FinNotInvolved: number;

  FinResolved: number;
  FinResolvedAndPositiveCSAT: number;
  FinResolvedAndNegativeCSAT: number;
  FinResolvedAndNeutralCSAT: number;

  FinAbandoned: number;
  FinAndTeammate: number;
  FinEscalationRulesAndGuidance: number;
  FinEscalationUserRequested: number;
  FinProcedureHandoff: number;
}

export const FinPerformanceFunnelConfig = {
  get filters() {
    return filters;
  },

  chartSeriesFilters(useSankeyFilterUpdate = false) {
    let finAbandonedFilter = useSankeyFilterUpdate
      ? this.filters.finPending
      : and(
          this.filters.finNotResolved,
          this.filters.teammateWasNeverInvolved,
          not(this.filters.finProcedureHandoff),
        );
    let finAndTeammateFilter = useSankeyFilterUpdate
      ? this.filters.finAndTeammateNew
      : this.filters.finAndTeammate;

    return [
      config('conversations'),

      config('Chat', this.filters.chatConversations),
      config('Email', this.filters.emailConversations),
      config('Phone', this.filters.phoneConversations),
      config('Android', this.filters.androidConversations),
      config('iOS', this.filters.iosConversations),
      config('Twitter', this.filters.twitterConversations),
      config('FacebookMessenger', this.filters.facebookMessengerConversations),
      config('Facebook', this.filters.facebookConversations),
      config('WhatsApp', this.filters.whatsappConversations),
      config('Instagram', this.filters.instagramConversations),
      config('PhoneSwitch', this.filters.phoneSwitchConversations),
      config('SMS', this.filters.smsConversations),
      config('ZendeskTicket', this.filters.zendeskTicketConversations),
      config('SalesforceCase', this.filters.salesforceCaseConversations),
      config('ZendeskMessaging', this.filters.zendeskMessagingConversations),
      config('Slack', this.filters.slackConversations),
      config('Unknown', this.filters.unknownConversations),

      config('ChatAndFinInvolved', and(this.filters.chatConversations, this.filters.finInvolved)),
      config('EmailAndFinInvolved', and(this.filters.emailConversations, this.filters.finInvolved)),
      config('PhoneAndFinInvolved', and(this.filters.phoneConversations, this.filters.finInvolved)),
      config(
        'AndroidAndFinInvolved',
        and(this.filters.androidConversations, this.filters.finInvolved),
      ),
      config('iOSAndFinInvolved', and(this.filters.iosConversations, this.filters.finInvolved)),
      config(
        'TwitterAndFinInvolved',
        and(this.filters.twitterConversations, this.filters.finInvolved),
      ),
      config(
        'ChatAndFinNotInvolved',
        and(this.filters.chatConversations, this.filters.finNotInvolved),
      ),
      config(
        'EmailAndFinNotInvolved',
        and(this.filters.emailConversations, this.filters.finNotInvolved),
      ),
      config(
        'PhoneAndFinNotInvolved',
        and(this.filters.phoneConversations, this.filters.finNotInvolved),
      ),
      config(
        'AndroidAndFinNotInvolved',
        and(this.filters.androidConversations, this.filters.finNotInvolved),
      ),
      config(
        'iOSAndFinNotInvolved',
        and(this.filters.iosConversations, this.filters.finNotInvolved),
      ),
      config(
        'TwitterAndFinNotInvolved',
        and(this.filters.twitterConversations, this.filters.finNotInvolved),
      ),
      config(
        'FacebookMessengerAndFinInvolved',
        and(this.filters.facebookMessengerConversations, this.filters.finInvolved),
      ),
      config(
        'FacebookMessengerAndFinNotInvolved',
        and(this.filters.facebookMessengerConversations, this.filters.finNotInvolved),
      ),
      config(
        'FacebookAndFinInvolved',
        and(this.filters.facebookConversations, this.filters.finInvolved),
      ),
      config(
        'FacebookAndFinNotInvolved',
        and(this.filters.facebookConversations, this.filters.finNotInvolved),
      ),
      config(
        'WhatsAppAndFinInvolved',
        and(this.filters.whatsappConversations, this.filters.finInvolved),
      ),
      config(
        'WhatsAppAndFinNotInvolved',
        and(this.filters.whatsappConversations, this.filters.finNotInvolved),
      ),
      config(
        'InstagramAndFinInvolved',
        and(this.filters.instagramConversations, this.filters.finInvolved),
      ),
      config(
        'InstagramAndFinNotInvolved',
        and(this.filters.instagramConversations, this.filters.finNotInvolved),
      ),
      config(
        'PhoneSwitchAndFinInvolved',
        and(this.filters.phoneSwitchConversations, this.filters.finInvolved),
      ),
      config(
        'PhoneSwitchAndFinNotInvolved',
        and(this.filters.phoneSwitchConversations, this.filters.finNotInvolved),
      ),
      config('SMSAndFinInvolved', and(this.filters.smsConversations, this.filters.finInvolved)),
      config(
        'SMSAndFinNotInvolved',
        and(this.filters.smsConversations, this.filters.finNotInvolved),
      ),
      config(
        'ZendeskTicketAndFinInvolved',
        and(this.filters.zendeskTicketConversations, this.filters.finInvolved),
      ),
      config(
        'ZendeskTicketAndFinNotInvolved',
        and(this.filters.zendeskTicketConversations, this.filters.finNotInvolved),
      ),
      config(
        'SalesforceCaseAndFinInvolved',
        and(this.filters.salesforceCaseConversations, this.filters.finInvolved),
      ),
      config(
        'SalesforceCaseAndFinNotInvolved',
        and(this.filters.salesforceCaseConversations, this.filters.finNotInvolved),
      ),
      config(
        'ZendeskMessagingAndFinInvolved',
        and(this.filters.zendeskMessagingConversations, this.filters.finInvolved),
      ),
      config(
        'ZendeskMessagingAndFinNotInvolved',
        and(this.filters.zendeskMessagingConversations, this.filters.finNotInvolved),
      ),
      config('SlackAndFinInvolved', and(this.filters.slackConversations, this.filters.finInvolved)),
      config(
        'SlackAndFinNotInvolved',
        and(this.filters.slackConversations, this.filters.finNotInvolved),
      ),
      config(
        'UnknownAndFinInvolved',
        and(this.filters.unknownConversations, this.filters.finInvolved),
      ),
      config(
        'UnknownAndFinNotInvolved',
        and(this.filters.unknownConversations, this.filters.finNotInvolved),
      ),

      config('FinInvolved', this.filters.finInvolved),
      config('FinNotInvolved', this.filters.finNotInvolved),

      // resolution flows
      config('FinInvolvedAndFinResolved', and(this.filters.finInvolved, this.filters.finResolved)),

      config('FinResolved', this.filters.finResolved),
      config(
        'FinResolvedAndPositiveCSAT',
        and(this.filters.finResolved, this.filters.positiveCSAT),
      ),
      config('FinResolvedAndNeutralCSAT', and(this.filters.finResolved, this.filters.neutralCSAT)),
      config(
        'FinResolvedAndNegativeCSAT',
        and(this.filters.finResolved, this.filters.negativeCSAT),
      ),

      config('FinAbandoned', and(this.filters.finInvolved, finAbandonedFilter)),
      config('FinAndTeammate', finAndTeammateFilter),
      ...(useSankeyFilterUpdate
        ? [
            config(
              'FinEscalationRulesAndGuidance',
              and(this.filters.finRoutedToTeam, this.filters.finEscalationRulesOrGuidanceApplied),
            ),
            config(
              'FinEscalationUserRequested',
              and(this.filters.finRoutedToTeam, this.filters.finEscalationUserRequested),
            ),
          ]
        : []),
      config(
        'FinProcedureHandoff',
        and(this.filters.finInvolved, this.filters.finProcedureHandoff),
      ),
      config(
        'FinInvolvedAndFinProcedureHandoff',
        and(this.filters.finInvolved, this.filters.finProcedureHandoff),
      ),
    ];
  },
  chartSeries(useSankeyFilterUpdate = false) {
    return this.chartSeriesFilters(useSankeyFilterUpdate).map((config) => config.series);
  },

  getChartSeriesWithLabels(intl: IntlService, useSankeyFilterUpdate = false) {
    return this.chartSeriesFilters(useSankeyFilterUpdate).map((config) => ({
      ...config.series,
      label: intl.t(`ai-agent.analyze.performance.funnel-metrics.${config.name}`),
    }));
  },

  getMetricsFromChartDataRequest(chartData: any, useSankeyFilterUpdate = false) {
    let result = {} as Metrics;
    let seriesFilters = this.chartSeriesFilters(useSankeyFilterUpdate);
    for (let i = 0; i < chartData.length; i++) {
      let seriesName = seriesFilters[i].name as keyof Metrics;
      let seriesData = chartData[i].data[0];
      result[seriesName] = seriesData;
    }
    return result;
  },
};
